// services/LoginService.ts
import axios from "axios";
import * as CONSTANTS from "../utils/constants/constants";
import { Credentials } from "../interfaces/user/Credentials";
import { Token } from "../interfaces/user/Token";

export type SSOConfigResponse = {
  organization_id: string;
  organization_name: string;
  login_domain: string;
  sso_enabled: boolean;
  sso_server: {
    server_id: string;
    server_name: string;
    server_url: string;
    oidc: {
      issuer?: string;
      client_id?: string;
      authorization_endpoint?: string;
      token_endpoint?: string;
      userinfo_endpoint?: string;
      jwks_uri?: string;
      scope?: string;
      response_type?: string;
      redirect_uri?: string;
      use_pkce?: boolean;
      use_par?: boolean;
      pushed_authorization_request_endpoint?: string;
      end_session_endpoint?: string;
      post_logout_redirect_uri?: string;
    };
  } | null;
};

type SSOLinkResponse = {
  linked: boolean;
  link: {
    provider: string;
    issuer: string;
    email: string;
    subject: string;
    organization: string;
    linked_at: string;
  } | null;
};

type SSOFlowMode = "login" | "link";

export type SSOTransaction = {
  mode: SSOFlowMode;
  state: string;
  nonce: string;
  verifier: string;
  email: string;
  organization_id: string;
  redirect_uri: string;
  created_at: number;
  expires_at: number;
};

type SSOAuthorizationResponse = {
  organization_id: string;
  request_uri: string;
  expires_in: number;
  authorization_url: string;
};

class SSORequestError extends Error {
  constructor(public readonly status?: number) {
    super(
      status === 502
        ? "The SSO provider is unavailable. Please try again later."
        : status === 401
          ? "You must be logged in to link an SSO account."
          : status === 403
            ? "SSO is disabled for your organization."
            : status === 404
              ? "No SSO organization was found."
              : status === 400
                ? "The SSO request was rejected. Please restart sign-in or contact your administrator."
                : "Unable to complete the SSO request. Please try again.",
    );
  }
}

function ssoRequestError(error: unknown): SSORequestError {
  return new SSORequestError(
    axios.isAxiosError(error) ? error.response?.status : undefined,
  );
}

const SSO_TX_STORAGE_KEY = "sso_auth_transaction";
const SSO_TRANSACTION_LIFETIME_MS = 10 * 60 * 1000;

function randomString(): string {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes.buffer);
}

function base64UrlEncode(arrayBuffer: ArrayBuffer): string {
  const bytes = new Uint8Array(arrayBuffer);
  let str = "";
  bytes.forEach((byte) => {
    str += String.fromCharCode(byte);
  });
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function createCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(digest);
}

export class LoginService {
  static getSSOErrorMessage(error: unknown): string {
    return error instanceof SSORequestError
      ? error.message
      : "Unable to complete SSO authentication. Please restart sign-in or contact your administrator.";
  }

  static async login(credentials: Credentials): Promise<Token | null> {
    try {
      const response = await axios.post<Token>(
        CONSTANTS.HOST + "api/o/token/",
        credentials,
      );
      return response.data;
    } catch (err: any) {
      console.error(err);
      return null;
    }
  }

  static logout() {
    // Optionally, invalidate the server-side session or token
    return axios
      .post(CONSTANTS.HOST + "auth/logout/")
      .then((response) => {
        console.log("Logged out on server side.");
      })
      .catch((error) => {
        console.error("Error logging out on server side:", error);
      });
  }

  static async getSSOConfigByEmail(
    email: string,
    mode: SSOFlowMode = "login",
    accessToken?: string,
  ): Promise<SSOConfigResponse | null> {
    if (mode === "link" && !accessToken) {
      throw new SSORequestError(401);
    }
    try {
      const response = await axios.get<SSOConfigResponse>(
        CONSTANTS.HOST + "api/v1/sso/config/",
        {
          params: { email, mode },
          ...(mode === "link" && {
            headers: { authorization: "Bearer " + accessToken },
          }),
        },
      );
      return response.data;
    } catch (error) {
      if (
        mode === "login" &&
        axios.isAxiosError(error) &&
        error.response?.status === 404
      ) {
        return null;
      }
      throw ssoRequestError(error);
    }
  }

  static async startSSOFlow(
    config: SSOConfigResponse,
    email: string,
    mode: SSOFlowMode,
    accessToken?: string,
  ): Promise<void> {
    LoginService.clearStoredSSOTransaction();
    if (mode === "link" && !accessToken) {
      throw new SSORequestError(401);
    }
    if (
      !config.sso_enabled ||
      !config.sso_server?.oidc?.authorization_endpoint ||
      !config.sso_server?.oidc?.client_id
    ) {
      throw new Error("SSO is not configured for this organization");
    }

    const verifier = randomString();
    const challenge = await createCodeChallenge(verifier);
    const state = randomString();
    const nonce = randomString();
    const redirectUri =
      config.sso_server.oidc.redirect_uri ||
      `${window.location.origin}${process.env.PUBLIC_URL || ""}/login/sso/callback`;

    if (config.sso_server.oidc.use_par && !config.sso_server.oidc.redirect_uri) {
      throw new Error("SSO redirect URI is not configured");
    }

    const createdAt = Date.now();
    const transaction: SSOTransaction = {
      mode,
      state,
      nonce,
      verifier,
      email,
      organization_id: config.organization_id,
      redirect_uri: redirectUri,
      created_at: createdAt,
      expires_at: createdAt + SSO_TRANSACTION_LIFETIME_MS,
    };

    try {
      sessionStorage.setItem(SSO_TX_STORAGE_KEY, JSON.stringify(transaction));

      if (config.sso_server.oidc.use_par) {
        const response = await axios.post<SSOAuthorizationResponse>(
          CONSTANTS.HOST + "api/v1/sso/authorize/",
          {
            ...(mode === "login" && { organization_id: config.organization_id }),
            mode,
            state,
            nonce,
            code_challenge: challenge,
            code_challenge_method: "S256",
            login_hint: email,
          },
          mode === "link"
            ? { headers: { authorization: "Bearer " + accessToken } }
            : undefined,
        );
        const authorization = response.data;
        if (
          !authorization ||
          typeof authorization.organization_id !== "string" ||
          !authorization.organization_id ||
          (mode === "login" &&
            authorization.organization_id !== config.organization_id) ||
          typeof authorization.request_uri !== "string" ||
          !authorization.request_uri ||
          !Number.isInteger(authorization.expires_in) ||
          authorization.expires_in <= 0 ||
          typeof authorization.authorization_url !== "string"
        ) {
          throw new Error("Invalid SSO authorization response");
        }
        const authorizationUrl = new URL(authorization.authorization_url);
        const configuredUrl = new URL(
          config.sso_server.oidc.authorization_endpoint,
        );
        const parameters = Array.from(authorizationUrl.searchParams.keys());
        if (
          authorizationUrl.origin !== configuredUrl.origin ||
          authorizationUrl.pathname !== configuredUrl.pathname ||
          authorizationUrl.hash ||
          parameters.length !== 2 ||
          !parameters.includes("client_id") ||
          !parameters.includes("request_uri") ||
          authorizationUrl.searchParams.get("client_id") !==
            config.sso_server.oidc.client_id ||
          authorizationUrl.searchParams.get("request_uri") !==
            authorization.request_uri
        ) {
          throw new Error("Invalid SSO authorization URL");
        }
        sessionStorage.setItem(
          SSO_TX_STORAGE_KEY,
          JSON.stringify({
            ...transaction,
            organization_id: authorization.organization_id,
          }),
        );
        window.location.assign(authorization.authorization_url);
        return;
      }

      const params = new URLSearchParams();
      params.set("client_id", config.sso_server.oidc.client_id);
      params.set("redirect_uri", redirectUri);
      params.set("response_type", config.sso_server.oidc.response_type || "code");
      params.set("scope", config.sso_server.oidc.scope || "openid profile email");
      params.set("state", state);
      params.set("nonce", nonce);
      params.set("login_hint", email);
      params.set("code_challenge", challenge);
      params.set("code_challenge_method", "S256");

      window.location.assign(
        `${config.sso_server.oidc.authorization_endpoint}?${params.toString()}`,
      );
    } catch (error) {
      LoginService.clearStoredSSOTransaction();
      throw ssoRequestError(error);
    }
  }

  static consumeSSOTransaction(state: string): SSOTransaction | null {
    const raw = sessionStorage.getItem(SSO_TX_STORAGE_KEY);
    LoginService.clearStoredSSOTransaction();
    if (!raw) {
      return null;
    }
    let transaction: unknown;
    try {
      transaction = JSON.parse(raw);
    } catch {
      return null;
    }
    if (typeof transaction !== "object" || transaction === null) {
      return null;
    }
    const tx = transaction as Partial<SSOTransaction>;
    const now = Date.now();
    if (
      (tx.mode !== "login" && tx.mode !== "link") ||
      typeof tx.state !== "string" ||
      !tx.state ||
      tx.state.length > 512 ||
      tx.state !== state ||
      typeof tx.nonce !== "string" ||
      !tx.nonce ||
      tx.nonce.length > 512 ||
      typeof tx.verifier !== "string" ||
      !/^[A-Za-z0-9._~-]{43,128}$/.test(tx.verifier) ||
      typeof tx.email !== "string" ||
      typeof tx.organization_id !== "string" ||
      !tx.organization_id ||
      typeof tx.redirect_uri !== "string" ||
      !tx.redirect_uri ||
      typeof tx.created_at !== "number" ||
      !Number.isFinite(tx.created_at) ||
      tx.created_at > now ||
      typeof tx.expires_at !== "number" ||
      !Number.isFinite(tx.expires_at) ||
      tx.expires_at <= now ||
      tx.expires_at !== tx.created_at + SSO_TRANSACTION_LIFETIME_MS
    ) {
      return null;
    }
    return {
      mode: tx.mode,
      state: tx.state,
      nonce: tx.nonce,
      verifier: tx.verifier,
      email: tx.email,
      organization_id: tx.organization_id,
      redirect_uri: tx.redirect_uri,
      created_at: tx.created_at,
      expires_at: tx.expires_at,
    };
  }

  static clearStoredSSOTransaction(): void {
    sessionStorage.removeItem(SSO_TX_STORAGE_KEY);
  }

  static async exchangeSSOAuthorizationCode(payload: {
    authorization_code: string;
    code_verifier: string;
    organization_id: string;
    redirect_uri: string;
    expected_nonce: string;
    email?: string;
  }): Promise<Token> {
    try {
      const response = await axios.post<Token>(
        CONSTANTS.HOST + "api/v1/sso/token/",
        payload,
      );
      return response.data;
    } catch (error) {
      throw ssoRequestError(error);
    }
  }

  static async getMySSOLink(
    accessToken: string,
  ): Promise<SSOLinkResponse | null> {
    try {
      const response = await axios.get<SSOLinkResponse>(
        CONSTANTS.HOST + "api/v1/sso/link/",
        {
          headers: { authorization: "Bearer " + accessToken },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch SSO link:", error);
      return null;
    }
  }

  static async completeMySSOLink(
    accessToken: string,
    payload: {
      authorization_code: string;
      code_verifier: string;
      redirect_uri: string;
      expected_nonce: string;
    },
  ): Promise<SSOLinkResponse> {
    try {
      const response = await axios.post<SSOLinkResponse>(
        CONSTANTS.HOST + "api/v1/sso/link/complete/",
        payload,
        {
          headers: { authorization: "Bearer " + accessToken },
        },
      );
      return response.data;
    } catch (error) {
      throw ssoRequestError(error);
    }
  }

  static async unlinkMySSO(accessToken: string): Promise<boolean> {
    try {
      await axios.delete(CONSTANTS.HOST + "api/v1/sso/link/", {
        headers: { authorization: "Bearer " + accessToken },
      });
      return true;
    } catch (error) {
      console.error("Failed to unlink SSO account:", error);
      return false;
    }
  }
}
